import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Radio, RadioGroup,
  Select, TablePagination, TextField, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSession } from '@/context/SessionContext';
import { useUser } from '@/context/UserContext';
import { SortFilterDialog, type SortFilterOption, type SortFilterValue } from '@/components/shared/SortFilterDialog';
import { getInventoryGrading, getInventoryGradingUoms, convertInventoryGrading, updateInventoryGrading, type InventoryGradingItem, type InventoryGradingMutationItem } from '@/services/inventory/inventoryGradingService';
import './inventory-grading.css';

type Notice = 'Loading' | 'Ready' | 'No Records' | 'Error' | 'Timeout';
type GradingRow = InventoryGradingItem & {
  rowId: string;
  location: string;
  unrestrictedSupply: number;
  qualityInspection: number;
  blockedSupply: number;
  uom: string;
  amountToConvert: number | null;
};
type DialogMode = 'filter' | 'sort' | null;
type Grade = 'unrestricted' | 'qualityInspection' | 'blocked';

const FILTER_FIELDS: SortFilterOption[] = [
  { property: 'MaterialCode', label: 'Material' },
  { property: 'MaterialDescription', label: 'Material Description' },
  { property: 'Plant', label: 'Location Code' },
  { property: 'RailCar', label: 'Railcar ID' },
  { property: 'Batch', label: 'Batch ID' },
  { property: 'LoadDate', label: 'Write-on Date' },
  { property: 'PlanningUnit', label: 'Planning Unit' },
  { property: 'ProductSubGroup', label: 'Product Sub Family' },
  { property: 'ProductFamily', label: 'Product Family' },
  { property: 'ShowAll', label: 'Show All' },
];
const SORT_FIELDS = FILTER_FIELDS.filter(({ property }) => property !== 'ShowAll');
const asNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(String(value ?? '').replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : 0;
};
const parseAmount = (value: string | undefined) => Number.parseFloat(String(value ?? '').replaceAll(',', ''));
const rowKey = (row: InventoryGradingItem, index: number) => [row.source, row.materialCode, row.plant, row.storageLocation, row.batch, row.railCar, index].map((value) => String(value ?? '')).join('|');
const adaptRow = (item: InventoryGradingItem, index: number): GradingRow => ({
  ...item,
  rowId: rowKey(item, index),
  location: String(item.plant ?? ''),
  unrestrictedSupply: asNumber(item.unrestrictedSupplyQuantity),
  qualityInspection: asNumber(item.inQualityInspectionQuantity),
  blockedSupply: asNumber(item.blockedSupplyQuantity),
  uom: String(item.baseUnitOfMeasure ?? ''),
  amountToConvert: null,
});
const isRailcar = (row: GradingRow) => Boolean(row.railCar) && row.railCar !== '';
const quantityText = (value: number) => value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 5 });
const materialText = (value: unknown) => String(value ?? '-').replace(/\b0+/g, '');
const dateText = (value: unknown) => {
  if (!value) return '-';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};
const toMutationItem = (row: GradingRow): InventoryGradingMutationItem => ({
  source: row.source as string | undefined,
  materialCode: row.materialCode as string | undefined,
  batch: row.batch,
  plant: row.plant,
  railCar: row.railCar,
  blockedSupplyQuantity: row.blockedSupply,
  unrestrictedSupplyQuantity: row.unrestrictedSupply,
  inQualityInspectionQuantity: row.qualityInspection,
  baseUnitOfMeasure: row.uom,
  storageLocation: row.storageLocation as string | undefined,
});

export const InventoryGradingPage: React.FC = () => {
  const { userData } = useUser();
  const { isLoading: sessionLoading } = useSession();
  const source = userData.region || userData.product;
  const uomSource = userData.product.toLowerCase() === 'chemicals' ? 'G9P' : userData.region;
  const [uom, setUom] = useState('KG');
  const [uoms, setUoms] = useState<string[]>(['KG']);
  const [rows, setRows] = useState<GradingRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [notice, setNotice] = useState<Notice>('Loading');
  const [filters, setFilters] = useState<SortFilterValue[]>([]);
  const [sorts, setSorts] = useState<SortFilterValue[]>([]);
  const [dialog, setDialog] = useState<DialogMode>(null);
  const [draftFilters, setDraftFilters] = useState<SortFilterValue[]>([]);
  const [draftSorts, setDraftSorts] = useState<SortFilterValue[]>([]);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState('');
  const [mutating, setMutating] = useState<string | null>(null);
  const [overrideRow, setOverrideRow] = useState<GradingRow | null>(null);
  const [overrideValues, setOverrideValues] = useState({ unrestricted: '', qualityInspection: '', blocked: '' });
  const [overrideGrade, setOverrideGrade] = useState<Grade>('qualityInspection');
  const sequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);

  const loadRows = useCallback(async (nextPage: number, nextFilters: SortFilterValue[], nextSorts: SortFilterValue[], nextUom: string, nextPageSize: number) => {
    sequence.current += 1;
    const requestId = sequence.current;
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setPage(nextPage);
    setNotice('Loading');
    setRows([]);
    setCount(0);
    setAmounts({});
    setErrorMessage('');
    try {
      const sorting = nextSorts.length
        ? nextSorts.map(({ property, value }) => ({ columnName: property, value }))
        : [
            { columnName: 'MaterialDescription', value: 'ASC' },
            { columnName: 'Plant', value: 'ASC' },
            { columnName: 'Batch', value: 'DESC' },
          ];
      const response = await getInventoryGrading({
        unitOfMeasure: nextUom,
        filters: nextFilters.map(({ property, value }) => ({ columnName: property, value })),
        searchFilters: null,
        sorting,
        pageNumber: nextPage + 1,
        linesPerPage: nextPageSize,
      }, controller.signal);
      if (requestId !== sequence.current || controller.signal.aborted) return;
      const mapped = (response.data ?? []).map(adaptRow);
      setRows(mapped);
      setCount(Number(response.count ?? 0));
      setNotice(Number(response.count ?? 0) > 0 ? 'Ready' : 'No Records');
    } catch (error: any) {
      if (controller.signal.aborted || requestId !== sequence.current) return;
      const detail = typeof error?.response?.data === 'string' ? error.response.data : String(error?.message ?? error);
      const timeout = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' || detail.includes('Timeout') || /timed out/i.test(detail);
      setErrorMessage(detail);
      setNotice(timeout ? 'Timeout' : 'Error');
    } finally {
      if (requestId === sequence.current) activeRequest.current = null;
    }
  }, [source, userData.userId]);

  useEffect(() => {
    if (sessionLoading) return undefined;
    const controller = new AbortController();
    void getInventoryGradingUoms(uomSource, controller.signal).then((values) => {
      if (!controller.signal.aborted) setUoms(Array.from(new Set(['KG', ...values])));
    }).catch(() => undefined);
    void loadRows(0, filters, sorts, uom, pageSize);
    return () => {
      controller.abort();
      activeRequest.current?.abort();
      sequence.current += 1;
    };
    // Initial load is repeated when the selected user/source changes.
  }, [sessionLoading, uomSource, loadRows]);

  const applyFilters = (values: SortFilterValue[]) => {
    setFilters(values);
    setDialog(null);
    void loadRows(0, values, sorts, uom, pageSize);
  };
  const applySorts = (values: SortFilterValue[]) => {
    setSorts(values);
    setDialog(null);
    void loadRows(0, filters, values, uom, pageSize);
  };
  const refresh = () => void loadRows(0, filters, sorts, uom, pageSize);
  const changePage = (_event: unknown, nextPage: number) => void loadRows(nextPage, filters, sorts, uom, pageSize);
  const replaceRow = (current: GradingRow, raw: InventoryGradingItem) => {
    setRows((previous) => {
      const index = previous.findIndex((item) => item.rowId === current.rowId);
      if (index < 0) return previous;
      const next = [...previous];
      next[index] = adaptRow(raw, index);
      return next;
    });
  };
  const mutateSupply = async (row: GradingRow, action: 'block' | 'unrestrict') => {
    const amount = isRailcar(row) ? row.qualityInspection : parseAmount(amounts[row.rowId]);
    if (!Number.isFinite(amount) || amount <= 0 || amount > row.qualityInspection
      || !row.materialCode || !row.plant || !row.uom) return;
    setMutating(row.rowId);
    setErrorMessage('');
    try {
      const updated = await convertInventoryGrading(action, {
        amount,
        item: {
          materialCode: row.materialCode,
          batch: row.batch || ' ',
          plant: row.plant,
          storageLocation: row.storageLocation || ' ',
          railCar: row.railCar ?? null,
          baseUnitOfMeasure: row.uom,
          unrestrictedSupplyQuantity: row.unrestrictedSupply,
          inQualityInspectionQuantity: row.qualityInspection,
          blockedSupplyQuantity: row.blockedSupply,
        },
      });
      replaceRow(row, updated);
      console.info(`Inventory grading ${action} completed`, updated);
    } catch (error) {
      console.error(`Inventory grading ${action} failed`, error);
      setErrorMessage(`Unable to ${action === 'block' ? 'block' : 'unrestrict'} supply. Please refresh and try again.`);
    } finally {
      setMutating(null);
    }
  };
  const openOverride = (row: GradingRow) => {
    setOverrideRow(row);
    setOverrideValues({ unrestricted: String(row.unrestrictedSupply), qualityInspection: String(row.qualityInspection), blocked: String(row.blockedSupply) });
    setOverrideGrade(row.unrestrictedSupply > 0 ? 'unrestricted' : row.qualityInspection > 0 ? 'qualityInspection' : 'blocked');
  };
  const overrideNumbers = useMemo(() => ({
    unrestricted: parseAmount(overrideValues.unrestricted),
    qualityInspection: parseAmount(overrideValues.qualityInspection),
    blocked: parseAmount(overrideValues.blocked),
  }), [overrideValues]);
  const overrideIsValid = overrideRow ? isRailcar(overrideRow) || (
    Object.values(overrideNumbers).every(Number.isFinite)
    && overrideNumbers.unrestricted + overrideNumbers.qualityInspection + overrideNumbers.blocked
      === overrideRow.unrestrictedSupply + overrideRow.qualityInspection + overrideRow.blockedSupply
  ) : false;
  const saveOverride = async () => {
    if (!overrideRow || !overrideIsValid) return;
    const row = overrideRow;
    const updatedRow = { ...row };
    if (isRailcar(row)) {
      const amount = row.unrestrictedSupply > 0 ? row.unrestrictedSupply : row.qualityInspection > 0 ? row.qualityInspection : row.blockedSupply;
      updatedRow.unrestrictedSupply = overrideGrade === 'unrestricted' ? amount : 0;
      updatedRow.qualityInspection = overrideGrade === 'qualityInspection' ? amount : 0;
      updatedRow.blockedSupply = overrideGrade === 'blocked' ? amount : 0;
    } else {
      updatedRow.unrestrictedSupply = overrideNumbers.unrestricted;
      updatedRow.qualityInspection = overrideNumbers.qualityInspection;
      updatedRow.blockedSupply = overrideNumbers.blocked;
    }
    setOverrideRow(null);
    setMutating(row.rowId);
    try {
      const updated = await updateInventoryGrading(toMutationItem(updatedRow));
      replaceRow(row, updated);
    } catch (error) {
      console.error('Inventory grading override failed', error);
      setErrorMessage('Unable to update grading values. Please refresh and try again.');
    } finally {
      setMutating(null);
    }
  };

  const ready = notice === 'Ready' || notice === 'No Records';
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const noRows = rows.length === 0;

  return (
    <Box className="inventory-grading-page">
      <Box className="im-page-header"><Typography component="h1" className="im-page-header-title">Inventory Grading</Typography></Box>
      <Box className="grading-content">
        {errorMessage && <Alert severity="error" onClose={() => setErrorMessage('')}>{errorMessage}</Alert>}
        <Box className="grading-toolbar">
          <Button variant="contained" disabled={!ready} onClick={refresh}>REFRESH TABLE</Button>
          <Box className="grading-toolbar-actions">
            <FormControl variant="standard" size="small" className="grading-uom-select">
              <InputLabel id="grading-uom-label">UoM</InputLabel>
              <Select labelId="grading-uom-label" value={uom} onChange={(event) => {
                const nextUom = event.target.value;
                setUom(nextUom);
                void loadRows(0, filters, sorts, nextUom, pageSize);
              }}>
                {uoms.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => setDialog('sort')}>SORT COLUMNS</Button>
            <Button variant="outlined" onClick={() => setDialog('filter')}>FILTER COLUMNS</Button>
          </Box>
        </Box>
        <Paper className="grading-table-paper" elevation={0} square>
          <table className="grading-table" aria-label="Inventory grading" aria-busy={notice === 'Loading'}>
            <colgroup>
              <col className="grading-col-material" /><col className="grading-col-description" /><col className="grading-col-location" />
              <col className="grading-col-date" /><col className="grading-col-railcar" /><col className="grading-col-batch" />
              <col className="grading-col-quantity" /><col className="grading-col-quantity" /><col className="grading-col-quantity" />
              <col className="grading-col-uom" /><col className="grading-col-amount" /><col className="grading-col-unrestrict" />
              <col className="grading-col-block" /><col className="grading-col-edit" />
            </colgroup>
            <thead><tr>
              <th>Material</th><th>Material Description</th><th>Location</th><th>Write-on<br />Date</th><th>Railcar ID</th><th>Batch ID</th>
              <th>Unrestricted</th><th>Quality<br />Inspection</th><th>Blocked</th><th>UoM</th><th>Amount to Convert</th><th aria-label="Unrestrict supply" /><th aria-label="Block supply" /><th aria-label="Edit grading" />
            </tr></thead>
            <tbody>
              {rows.map((row) => {
                const railcar = isRailcar(row);
                const entered = parseAmount(amounts[row.rowId]);
                const canConvert = row.qualityInspection > 0 && (railcar || (Number.isFinite(entered) && entered > 0 && entered <= row.qualityInspection));
                return <tr key={row.rowId}>
                  <td>{materialText(row.materialCode)}</td>
                  <td title={String(row.materialDescription ?? '').length > 12 ? String(row.materialDescription) : undefined}>{row.materialDescription || '-'}</td>
                  <td>{row.plant || '-'}</td><td>{dateText(row.loadDate)}</td><td>{row.railCar || '-'}</td><td>{row.batch || '-'}</td>
                  <td>{quantityText(row.unrestrictedSupply)}</td><td className="grading-quality-value">{quantityText(row.qualityInspection)}</td><td>{quantityText(row.blockedSupply)}</td><td>{row.uom || '-'}</td>
                  <td>{railcar ? quantityText(row.qualityInspection) : <TextField
                    variant="standard" placeholder="Enter Amount" value={amounts[row.rowId] ?? ''} inputProps={{ 'aria-label': `Amount to convert for batch ${row.batch ?? ''}`, inputMode: 'decimal' }}
                    onChange={(event) => setAmounts((previous) => ({ ...previous, [row.rowId]: event.target.value }))}
                  />}</td>
                  <td><Button variant="outlined" disabled={!canConvert || mutating === row.rowId} onClick={() => void mutateSupply(row, 'unrestrict')}>UNRESTRICT SUPPLY</Button></td>
                  <td><Button variant="outlined" disabled={!canConvert || mutating === row.rowId} onClick={() => void mutateSupply(row, 'block')}>BLOCK SUPPLY</Button></td>
                  <td><Tooltip title="Edit grading"><IconButton aria-label={`Edit grading for batch ${row.batch ?? ''}`} disabled={mutating === row.rowId} onClick={() => openOverride(row)}><EditIcon /></IconButton></Tooltip></td>
                </tr>;
              })}
            </tbody>
            {noRows && notice !== 'Ready' && <tfoot><tr><td colSpan={14}><Box className="grading-notice" role="status" aria-live="polite">
              {notice === 'Loading' ? <><CircularProgress size={44} /><Typography>Loading table data</Typography></>
                : notice === 'No Records' ? <><Typography>No records found</Typography><Button variant="contained" onClick={() => setDialog('filter')}>EDIT FILTERS</Button></>
                  : <><Typography>{notice === 'Timeout' ? 'The request timed out.' : 'The table failed to load.'}</Typography><Box><Button variant="outlined" onClick={() => setDialog('filter')}>EDIT FILTERS</Button><Button variant="contained" onClick={refresh}>TRY AGAIN</Button></Box></>}
            </Box></td></tr></tfoot>}
          </table>
          <TablePagination component="div" className="grading-pagination" count={count} page={Math.min(page, totalPages - 1)} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100]} onPageChange={changePage} onRowsPerPageChange={(event) => {
            const size = Number(event.target.value);
            setPageSize(size);
            void loadRows(0, filters, sorts, uom, size);
          }} />
        </Paper>
      </Box>
      <SortFilterDialog open={dialog === 'filter'} mode="filter" fields={FILTER_FIELDS} activeFields={filters} onClose={() => setDialog(null)} onApply={applyFilters} />
      <SortFilterDialog open={dialog === 'sort'} mode="sort" fields={SORT_FIELDS} activeFields={sorts} onClose={() => setDialog(null)} onApply={applySorts} />
      <Dialog open={Boolean(overrideRow)} onClose={() => setOverrideRow(null)} className="grading-override-dialog" fullWidth maxWidth="sm">
        <DialogTitle>Override Grading Values For Batch #{overrideRow?.batch ?? ''}</DialogTitle>
        <DialogContent>
          <Typography className="grading-override-warning">Overrides should only be used to correct mistakes. Below are the current supply values for each grade.</Typography>
          {overrideRow && <Box className="grading-current-values">
            <Box><Typography>Unrestricted</Typography><b>{quantityText(overrideRow.unrestrictedSupply)}</b></Box>
            <Box><Typography>Quality Inspection</Typography><b>{quantityText(overrideRow.qualityInspection)}</b></Box>
            <Box><Typography>Blocked</Typography><b>{quantityText(overrideRow.blockedSupply)}</b></Box>
            <Box><Typography>UoM</Typography><b>{overrideRow.uom}</b></Box>
          </Box>}
          <Typography className="grading-override-instruction">Enter updated values for each grade</Typography>
          {overrideRow && (isRailcar(overrideRow) ? <RadioGroup row value={overrideGrade} onChange={(event) => setOverrideGrade(event.target.value as Grade)}>
            <FormControlLabel value="unrestricted" control={<Radio />} label="Unrestricted" />
            <FormControlLabel value="qualityInspection" control={<Radio />} label="Quality Inspection" />
            <FormControlLabel value="blocked" control={<Radio />} label="Blocked" />
          </RadioGroup> : <Box className="grading-override-inputs">
            <TextField label="Unrestricted" value={overrideValues.unrestricted} onChange={(event) => setOverrideValues((old) => ({ ...old, unrestricted: event.target.value }))} />
            <TextField label="Quality Inspection" value={overrideValues.qualityInspection} onChange={(event) => setOverrideValues((old) => ({ ...old, qualityInspection: event.target.value }))} />
            <TextField label="Blocked" value={overrideValues.blocked} onChange={(event) => setOverrideValues((old) => ({ ...old, blocked: event.target.value }))} />
          </Box>)}
        </DialogContent>
        <DialogActions><Button onClick={() => setOverrideRow(null)}>CANCEL</Button><Button variant="contained" disabled={!overrideIsValid} onClick={() => void saveOverride()}>OVERRIDE GRADES</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
